export interface ApiFieldError {
  field: string;
  message: string;
}

export interface ApiErrorResponse {
  error: {
    code: string;
    message: string;
    details?: ApiFieldError[];
  };
}

/**
 * Normalised application error type for all API failures.
 *
 * Use `instanceof ApiError` to distinguish API errors from unexpected
 * runtime errors in UI error boundaries and TanStack Query handlers.
 */
export class ApiError extends Error {
  public readonly status: number;
  public readonly code: string;
  public readonly details: ApiFieldError[];

  constructor(status: number, code: string, message: string, details: ApiFieldError[] = []) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
  }

  /** True when the server indicated the session is invalid or expired. */
  get isUnauthenticated(): boolean {
    return this.status === 401;
  }

  /** True when the user lacks permission (but IS authenticated). */
  get isForbidden(): boolean {
    return this.status === 403;
  }

  /** True for validation / field-level errors. */
  get isValidation(): boolean {
    return this.code === 'VALIDATION_ERROR';
  }

  /** True for rate-limit responses. */
  get isRateLimited(): boolean {
    return this.status === 429;
  }

  /** Get the first error message for a specific field, if any. */
  fieldError(field: string): string | undefined {
    return this.details.find((d) => d.field === field)?.message;
  }
}
